import { findBand, BAND_COLORS } from "../bands";

// ESSDAI — EULAR Sjögren's Syndrome Disease Activity Index.
// Twelve weighted domains; each domain is scored by activity level
// (no / low / moderate / high) and the domain contribution = weight × level.
// Total ESSDAI = sum of all domain contributions.
// ⚠️ Activity-level definitions per domain are detailed clinical criteria; here the
// levels are generic and the domain notes are hints. Verify against the ESSDAI
// definitions (Seror et al.) before clinical use.
const essdaiBands = [
    { label: "Low activity", color: BAND_COLORS.low, lt: 5 },
    { label: "Moderate activity", color: BAND_COLORS.moderate, lt: 14 },
    { label: "High activity", color: BAND_COLORS.high },
];

const LEVELS = ["No activity", "Low activity", "Moderate activity", "High activity"];

// levels = number of selectable activity levels (3 → no/low/moderate, 4 → +high).
const DOMAINS = [
    { key: "constitutional", label: "Constitutional", weight: 3, levels: 3, hint: "Exclusion of fever of infectious origin and voluntary weight loss." },
    { key: "lymphadenopathy", label: "Lymphadenopathy", weight: 4, levels: 4, hint: "Exclusion of infection." },
    { key: "glandular", label: "Glandular", weight: 2, levels: 3, hint: "Exclusion of stone or infection." },
    { key: "articular", label: "Articular", weight: 2, levels: 4, hint: "Exclusion of osteoarthritis." },
    { key: "cutaneous", label: "Cutaneous", weight: 3, levels: 4, hint: "Rate as 'no activity' stable long-lasting features related to damage." },
    { key: "pulmonary", label: "Pulmonary", weight: 5, levels: 4, hint: "Rate as 'no activity' stable long-lasting features related to damage, or respiratory involvement not related to the disease." },
    { key: "renal", label: "Renal", weight: 5, levels: 4, hint: "Rate as 'no activity' stable long-lasting features related to damage and renal involvement not related to the disease." },
    { key: "muscular", label: "Muscular", weight: 6, levels: 4, hint: "Exclusion of weakness due to corticosteroids." },
    { key: "pns", label: "Peripheral nervous system (PNS)", weight: 5, levels: 4, hint: "Rate as 'no activity' stable long-lasting features related to damage or PNS involvement not related to the disease." },
    { key: "cns", label: "Central nervous system (CNS)", weight: 5, levels: 4, hint: "Rate as 'no activity' stable long-lasting features related to damage or CNS involvement not related to the disease." },
    { key: "haematological", label: "Haematological", weight: 2, levels: 4, hint: "For anaemia, neutropenia, and thrombopenia, only autoimmune cytopenia." },
    { key: "biological", label: "Biological", weight: 1, levels: 3, hint: "IgG: immunoglobulin G." },
];

export const essdai = {
    id: "essdai",
    name: "ESSDAI",
    subtitle: "EULAR Sjögren's syndrome disease activity index",
    group: "Sjögren's syndrome",
    status: "ready",
    options: [],
    fields: DOMAINS.map((d) => ({
        key: d.key,
        type: "select",
        label: d.label,
        hint: d.hint,
        default: 0,
        // Option value carries the domain contribution (weight × activity level).
        choices: Array.from({ length: d.levels }, (_, i) => ({
            label: LEVELS[i],
            value: d.weight * i,
        })),
    })),
    compute: (v) => DOMAINS.reduce((sum, d) => sum + (Number(v[d.key]) || 0), 0),
    interpret: (score) => findBand(score, essdaiBands),
    bands: () => essdaiBands,
    about:
        "EULAR Sjögren's syndrome disease activity index (ESSDAI) is a reliable score for assessing the systemic features of Sjögren's syndrome. It combines 12 weighted domains, each scored by activity level (the domain score is its weight multiplied by the activity level). It has large sensitivity to change in patients whose disease activity improves. It correlates poorly with the patient score (ESSPRI), so both should be evaluated, but separately.",
    references: [
        "Seror R, et al. EULAR Sjögren's syndrome disease activity index (ESSDAI): development of a consensus systemic disease activity index. Ann Rheum Dis. 2010.",
    ],
};
